import {
  CheckboxGroup,
  EBehavior,
  ICheckboxGroupsChangeModel,
  ICheckboxGroupsItemChangeModel,
  NestedCheckboxGroupsWithSearchDesktop,
} from '@cian/nested-checkbox-groups';
import { FC, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { DropdownFilter, useDropdown } from 'shared/components/DropdownFilter';
import { useAppDispatch, useFilterChangeCallback, useShowSearchMetaTooltip } from 'shared/hooks';
import {
  selectBusinessAppointmentsGroups,
  selectBusinessAppointmentsLabel,
} from 'shared/selectors/filters/businessAppointments';
import { selectIsInlineFiltersRedesigned } from 'shared/selectors/redesigned/selectIsInlineFiltersRedesigned';
import { selectSpecialtyTypes } from 'shared/selectors/jsonQuery';
import { applyFilters } from 'shared/thunks/filters';
import { updateTags } from 'shared/thunks/tags';

import { FILTER_KEY } from './constants';

import styles from './BusinessAppointmentsCategoryContainer.css';

export const BusinessAppointmentsCategoryContainer: FC = () => {
  const groups = useSelector(selectBusinessAppointmentsGroups);
  const label = useSelector(selectBusinessAppointmentsLabel);
  const selectedIds = useSelector(selectSpecialtyTypes) ?? [];
  const isFiltersRedesignEnabled = useSelector(selectIsInlineFiltersRedesigned);

  const { opened, onOpen, onClose } = useDropdown(FILTER_KEY);
  const showTooltip = useShowSearchMetaTooltip(FILTER_KEY);
  const dispatch = useAppDispatch();

  const onChange = useFilterChangeCallback();
  const pendingApplyRef = useRef<Promise<void> | null>(null);

  const checkboxGroups = useMemo<CheckboxGroup[]>(
    () =>
      groups.map(group => ({
        label: group.name,
        value: group.groupType,
        items: group.items.map(item => ({ label: item.name, value: item.id })),
      })),
    [groups],
  );

  const apply = useCallback(
    (allItems: { value: number }[]) => {
      const ids = allItems.map(item => item.value);
      const promise = onChange({ action: 'setSpecialtyTypes', arguments: [ids.length > 0 ? ids : null] }).then(() => {
        dispatch(updateTags());
      });
      pendingApplyRef.current = promise;

      return promise;
    },
    [onChange, dispatch],
  );

  const handleClose = useCallback(async () => {
    onClose();
    const pending = pendingApplyRef.current;
    if (!pending) {
      return;
    }
    pendingApplyRef.current = null;
    await pending;
    dispatch(applyFilters());
  }, [onClose, dispatch]);

  const handleNestedClick = useCallback((model: ICheckboxGroupsItemChangeModel) => apply(model.allItems), [apply]);

  const handleTitleClick = useCallback((model: ICheckboxGroupsChangeModel) => apply(model.allItems), [apply]);

  if (groups.length === 0) {
    return null;
  }

  return (
    <DropdownFilter
      opened={opened}
      onOpen={onOpen}
      onClose={handleClose}
      buttonLabel={label}
      showTooltip={showTooltip}
      isFiltersRedesignEnabled={isFiltersRedesignEnabled}
      dropdownContent={
        <div className={styles['popup']}>
          <NestedCheckboxGroupsWithSearchDesktop
            behavior={EBehavior.Multiply}
            classNameContainer={styles['popup-content']}
            classNameNestedCheckboxGroups={styles['checkbox-groups']}
            groups={checkboxGroups}
            values={selectedIds}
            onNestedCheckboxClick={handleNestedClick}
            onTitleCheckboxClick={handleTitleClick}
          />
        </div>
      }
    />
  );
};
